import React, { ReactElement, useState, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch } from 'src/store/hooks'
import {
  createCAPCEAccreditation,
  fetchCAPCEAccreditation,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'

type CAPCECreationFormProps = {
  open: boolean
  onClose: () => void
  eventID: string
  eventName?: string
}

let topicAreas = [
  'Abdominal Emergencies',
  'Abuse',
  'ACLS',
  'Active Shooter',
  'Acute Coronary Syndromes',
  'Adult Respiratory Emergencies',
  'Advanced Airway',
  'AEMT Refresher',
  'Aeromedical Transport',
  'Allergy and Anaphylaxis',
  'ALS',
  'Altered Mental Status',
  'Ambulance Safety',
  'AMLS',
  'Anatomy and Physiology',
  'APLS',
  'Assessment',
  'Asthma',
  'At Risk Populations',
  'Autism',
  'Basic Airway',
  'Blood Borne Pathogens',
  'BLS',
  'Burns',
  'Capnography',
  'Cardiology',
  'CBRNE',
  'Cervical Injuries',
  'Chest Injuries',
  'CHF',
  'CNS Injury',
  'Communications',
  'Community Paramedicine',
  'Congenital Defects',
  'COPD',
  'Corona Virus',
  'COVID-19',
  'CPAP',
  'CPR',
  'Crew Resource Management',
  'Critical Care',
  'Critical Care Transport',
  'Critical Decision Making',
  'Culture of Safety',
  'Death',
  'Depression',
  'Diabetic Emergencies',
  'Disaster Management',
  'Diving and Hyperbarics',
  'Documentation',
  'Ebola',
  'Educator',
  'EKG Recognition',
  'Electrolyte Imbalance',
  'Emergency Child Birth',
  'Emergency Medical Dispatch (EMD)',
  'Emotional Resilience',
  'EMR Refresher Course',
  'EMS Self Defense',
  'EMT Refresher',
  'Endocrinology',
  'Envenomations',
  'Environmental Emergencies',
  'Ethics',
  'Evidence Based Guidelines',
  'Flight Physiology',
  'Fluid Resuscitation',
  'Fracture Management',
  'Geriatrics',
  'GI and GU Emergencies',
  'Gynecology',
  'Hazardous Materials',
  'Head Injuries',
  'Health and Wellness',
  'Heart Rhythms',
  'Hematology',
  'Hemorrhage Control',
  'Human Trafficking',
  'Hygiene Vaccinations',
  'Hypertension',
  'Immunology',
  'Infection Control',
  'Infectious Disease',
  'Intraosteous Infusion',
  'ITLS',
  'IV Therapy',
  'K9 Officer Care',
  'Leadership',
  'Legal',
  'LVAD',
  'Management Training',
  'Mass Violence',
  'Medical Director',
  'Medical Emergencies',
  'Medication Delivery',
  'MRSA',
  'Neonatology',
  'Neurology',
  'Nephrology',
  'Obesity',
  'Obstetrics',
  'Oncology',
  'Operations',
  'Opioids',
  'OSHA',
  'Oxygenation',
  'Pain Management',
  'PALS',
  'Paramedic Refresher',
  'Pediatric Assessment',
  'Pediatric Cardiac Arrest',
  'Pediatric Emergency Standards',
  'Pediatric Medical Emergencies',
  'Pediatric Respiratory Emergencies',
  'Pediatric Shock',
  'Pediatric Trauma',
  'Pediatrics',
  'Pharmacology',
  'Phlebotomy',
  'PHTLS',
  'Post Resuscitation Care',
  'Psychiatric Emergencies',
  'PTSD',
  'Quality Improvement',
  'Research',
  'Respiratory Emergencies',
  'Resuscitation',
  'Safety',
  'Seizures',
  'Sepsis',
  'Shock',
  'Skills',
  'Special Needs',
  'Special Topics',
  'Spinal Restriction',
  'Sports Injuries',
  'Stroke',
  'Tactical',
  'Terrorism Response',
  'Toxicology',
  'Traffic Incident Management',
  'Trauma',
  'Trauma Triage',
  'Traumatic Brain Injuries',
  'Triage',
  'Ultrasound',
  'Urology',
  'Ventilation',
  'Ventricular Assist Devices',
  'Wilderness Medicine',
]

let classTypes = [
  'ARV',
  'Cardiac',
  'Critical Care',
  'Education',
  'Medical',
  'OBGYN',
  'Operations',
  'Trauma',
  'Pediatrics',
  'Other',
]

export default ({
  open,
  onClose,
  eventID,
  eventName,
}: CAPCECreationFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [loading, setLoading] = useState(false)

  let form = useForm({
    defaultValues: {
      courseCode: '',
      expirationDate: '',
      applicationDate: '',
      submissionDate: '',
      topicArea: '',
      classType: '',
      allClearDate: '',
      goLiveDate: '',
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        let formattedData = {
          eventID,
          courseCode: value.courseCode,
          expirationDate: value.expirationDate
            ? new Date(value.expirationDate).toISOString()
            : null,
          applicationDate: value.applicationDate
            ? new Date(value.applicationDate).toISOString()
            : null,
          submissionDate: value.submissionDate
            ? new Date(value.submissionDate).toISOString()
            : null,
          topicArea: value.topicArea,
          classType: value.classType,
          allClearDate: value.allClearDate
            ? new Date(value.allClearDate).toISOString()
            : null,
          goLiveDate: value.goLiveDate
            ? new Date(value.goLiveDate).toISOString()
            : null,
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(createCAPCEAccreditation(formattedData))
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchCAPCEAccreditation(eventID))
        form.reset()
        setLoading(false)
        onClose()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create CAPCE accreditation:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    if (open) {
      // Set default dates - application date to today, expiration to 2 years from now
      let today = new Date().toISOString().split('T')[0]
      let twoYears = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0]

      form.setFieldValue('applicationDate', today)
      form.setFieldValue('expirationDate', twoYears)
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={() => {
        form.reset()
        setLoading(false)
        onClose()
      }}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>Create CAPCE Accreditation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 3, mt: 1 }}>
          <Typography color="text.secondary" variant="body2">
            Create CAPCE accreditation for {eventName || 'this event'}. All
            fields are required for compliance approval.
          </Typography>

          <form.Field
            name="courseCode"
            children={(field) => (
              <TextField
                label="Course Code"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                required
                fullWidth
                sx={modalInputSx}
                placeholder="e.g., CAPCE12345"
              />
            )}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <form.Field
              name="topicArea"
              children={(field) => (
                <FormControl fullWidth required>
                  <InputLabel>Topic Area</InputLabel>
                  <Select
                    value={field.state.value}
                    label="Topic Area"
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    sx={modalInputSx}
                  >
                    {topicAreas.map((area) => (
                      <MenuItem key={area} value={area}>
                        {area}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <form.Field
              name="classType"
              children={(field) => (
                <FormControl fullWidth required>
                  <InputLabel>Class Type</InputLabel>
                  <Select
                    value={field.state.value}
                    label="Class Type"
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    sx={modalInputSx}
                  >
                    {classTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          <Typography variant="h6" sx={{ mt: 2 }}>
            Important Dates
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <form.Field
              name="applicationDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Application Date"
                    value={field.state.value ? dayjs(field.state.value) : null}
                    onChange={(val) => {
                      if (!val) {
                        field.handleChange('')
                      } else {
                        field.handleChange(val.format('YYYY-MM-DD'))
                      }
                    }}
                    onClose={field.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: modalInputSx,
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors[0],
                        onBlur: field.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />

            <form.Field
              name="submissionDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Submission Date"
                    value={field.state.value ? dayjs(field.state.value) : null}
                    onChange={(val) => {
                      if (!val) {
                        field.handleChange('')
                      } else {
                        field.handleChange(val.format('YYYY-MM-DD'))
                      }
                    }}
                    onClose={field.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: modalInputSx,
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors[0],
                        onBlur: field.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />

            <form.Field
              name="expirationDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expiration Date"
                    value={field.state.value ? dayjs(field.state.value) : null}
                    onChange={(val) => {
                      if (!val) {
                        field.handleChange('')
                      } else {
                        field.handleChange(val.format('YYYY-MM-DD'))
                      }
                    }}
                    onClose={field.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: modalInputSx,
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors[0],
                        onBlur: field.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />

            <form.Field
              name="allClearDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="All Clear Date"
                    value={field.state.value ? dayjs(field.state.value) : null}
                    onChange={(val) => {
                      if (!val) {
                        field.handleChange('')
                      } else {
                        field.handleChange(val.format('YYYY-MM-DD'))
                      }
                    }}
                    onClose={field.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: modalInputSx,
                        onBlur: field.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />

            <form.Field
              name="goLiveDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Go Live Date"
                    value={field.state.value ? dayjs(field.state.value) : null}
                    onChange={(val) => {
                      if (!val) {
                        field.handleChange('')
                      } else {
                        field.handleChange(val.format('YYYY-MM-DD'))
                      }
                    }}
                    onClose={field.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: modalInputSx,
                        onBlur: field.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            form.reset()
            setLoading(false)
            onClose()
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={() => form.handleSubmit()}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create CAPCE Accreditation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
